import React, { Fragment, useState, useCallback, useEffect, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Btn, Breadcrumbs } from '../../AbstractElements';
import { tableColumns } from './ExperienceData';
import { Button, Form } from 'react-bootstrap';
import Search from '../../assets/images/svg/Search_SoS.svg';
import SearchIcon from '../../assets/images/svg/search_iconSos.svg';
import Calendar from '../../assets/images/svg/Calendar_SoS.svg';
import Filter from '../../assets/images/svg/Filter_SoS.svg';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { ServerPath } from '../../Constant';
import axios from 'axios';
import Swal from 'sweetalert2';
import EditExperience from './EditExperience';

const ExperienceTable = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleDelete, setToggleDelete] = useState(false);
    const [data, setData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const searchRef = useRef(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [showCalendar, setShowCalendar] = useState(false);
    const calendarRef = useRef(null);
    const [dateRange, setDateRange] = useState([
        {
            startDate: null,
            endDate: null,
            key: 'selection'
        }
    ]);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const filterOptions = [
        { label: 'All', value: 'all' },
        { label: 'Active', value: 'Active' },
        { label: 'Pending Moderation', value: 'Pending Moderation' },
        { label: 'Review', value: 'Review' },
        { label: 'Reported', value: 'Reported' },
        { label: 'Cancelled', value: 'Cancelled' }
    ];

    // Handle page changes
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Handle rows per page changes
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearch(false);
            }
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchExperiences = async () => {
            try {
                const response = await axios.get(`${ServerPath}/experienceAdmin/admin`);
                
                if (response.data.success === 1) {
                    const formattedData = response.data.data.map(exp => ({
                        ...exp,
                        experience_title: exp.experience_title,
                        author: exp.user_name,
                        submittedDate: new Date(exp.created_on).toLocaleDateString(),
                        updatedBy: exp.modified_by || exp.user_name,
                        updatedOn: new Date(exp.modified_on || exp.created_on).toLocaleDateString(),
                        status: exp.status || 'Pending',
                        // Store original values for searching
                        original_experience_title: exp.experience_title,
                        original_user_name: exp.user_name,
                        original_status: exp.status?.toLowerCase(),
                        original_date: exp.created_on,
                        action: (
                            <div className="action d-flex justify-content-center">
                                <EditExperience data={exp} />
                            </div>
                        ),
                    }));
                    
                    setData(formattedData);
                    setOriginalData(formattedData);
                } else {
                    Swal.fire('Error!', 'Failed to fetch experiences data', 'error');
                }
            } catch (error) {
                console.error('Error fetching experiences:', error);
                Swal.fire('Error!', 'Failed to fetch experiences data', 'error');
            }
        };
    
        fetchExperiences();
    }, []);

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const handleFilterChange = (value) => {
        setSelectedFilter(value);
        const currentDateRange = dateRange[0]?.startDate && dateRange[0]?.endDate ? dateRange[0] : null;
        applyFiltersAndSearch(searchValue, value, currentDateRange);
        setShowDropdown(false);
    };

    const handleDateRangeChange = (item) => {
        setDateRange([item.selection]);
        applyFiltersAndSearch(searchValue, selectedFilter, item.selection);
    };

    const clearDateFilter = () => {
        setDateRange([{
            startDate: null,
            endDate: null,
            key: 'selection'
        }]);
        applyFiltersAndSearch(searchValue, selectedFilter, null);
        setShowCalendar(false);
    };

    const handleSearch = (value) => {
        setSearchValue(value);
        const currentDateRange = dateRange[0]?.startDate && dateRange[0]?.endDate ? dateRange[0] : null;
        applyFiltersAndSearch(value, selectedFilter, currentDateRange);
    };

    const applyFiltersAndSearch = (searchValue, filterValue, dateValue) => {
        let filteredData = [...originalData];

        if (searchValue && searchValue.trim()) {
            const searchTerm = searchValue.toLowerCase();
            filteredData = filteredData.filter(item => {
                return (
                    String(item.original_experience_title || '').toLowerCase().includes(searchTerm) ||
                    String(item.original_user_name || '').toLowerCase().includes(searchTerm) ||
                    String(item.original_status || '').toLowerCase().includes(searchTerm)
                );
            });
        }

        if (filterValue && filterValue !== 'all') {
            filteredData = filteredData.filter(item =>
                item.original_status?.toLowerCase() === filterValue.toLowerCase()
            );
        }

        if (dateValue?.startDate && dateValue?.endDate) {
            filteredData = filteredData.filter(item => {
                if (!item.original_date) return true;
                const itemDate = new Date(item.original_date);
                const startOfDay = new Date(dateValue.startDate.setHours(0, 0, 0, 0));
                const endOfDay = new Date(dateValue.endDate.setHours(23, 59, 59, 999));

                if (startOfDay.getTime() === new Date(dateValue.endDate.setHours(0, 0, 0, 0)).getTime()) {
                    return itemDate >= startOfDay && itemDate <= endOfDay;
                }
                return itemDate >= startOfDay && itemDate <= endOfDay;
            });
        }

        setData(filteredData);
    };

    const handleMultiDelete = async () => {
        if (window.confirm(`Are you sure you want to delete the selected experiences?`)) {
            try {
                // Get the exp_ids from selected rows
                const expIds = selectedRows.map(row => row.exp_id);
                
                // Call the API to delete experiences
                const response = await axios.post(`${ServerPath}/experienceAdmin/delete`, {
                    expIds: expIds
                });
                
                if (response.data.success === 1) {
                    // Update UI only after successful deletion
                    setToggleDelete(!toggleDelete);
                    const updatedData = data.filter((item) => 
                        !selectedRows.find(row => row.exp_id === item.exp_id)
                    );
                    setData(updatedData);
                    setOriginalData(updatedData);
                    setSelectedRows([]);
                    
                    // Show success message
                    Swal.fire('Success', 'Experiences deleted successfully', 'success');
                    window.location.reload();
                } else {
                    Swal.fire('Error', 'Failed to delete experiences', 'error');
                }
            } catch (error) {
                console.error('Error deleting experiences:', error);
                Swal.fire('Error', 'Failed to delete experiences', 'error');
            }
        }
    };

    return (
        <Fragment>
            <Container fluid={true}>
                <div className='row align-items-center mb-1'>
                    <div className='col-9'>
                        <Breadcrumbs mainTitle="Experiences" parent="Community Management" title="Experiences List" />
                    </div>
                    <div className='col-3'>
                        <div className="d-flex justify-content-end gap-1">
                            {/* Search Component */}
                            <div className="position-relative" ref={searchRef}>
                                <button
                                    className="bg-transparent border-0"
                                    onClick={() => setShowSearch(true)}
                                >
                                    <img src={Search} alt="search" style={{ width: '36px', height: '36px' }} />
                                </button>

                                {showSearch && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '45px',
                                            left: '-180px',
                                            width: '220px',
                                            zIndex: 1000
                                        }}
                                    >
                                        <div
                                            style={{
                                                background: 'white',
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                padding: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}
                                        >
                                            <div
                                                style={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    background: '#F8F7FA',
                                                    borderRadius: '6px',
                                                    padding: '6px 12px'
                                                }}
                                            >
                                                <input
                                                    type="text"
                                                    placeholder="Search Here..."
                                                    value={searchValue}
                                                    onChange={(e) => handleSearch(e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        border: 'none',
                                                        background: 'transparent',
                                                        fontSize: '14px',
                                                        color: '#666',
                                                        outline: 'none'
                                                    }}
                                                    autoFocus
                                                />
                                                <button
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        padding: 0,
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                    onClick={() => handleSearch(searchValue)}
                                                >
                                                    <img src={SearchIcon} alt="search" style={{ width: '20px', height: '20px' }} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Calendar Component */}
                            <div className="position-relative" ref={calendarRef}>
                                <button
                                    className="bg-transparent border-0"
                                    onClick={() => setShowCalendar(!showCalendar)}
                                >
                                    <img src={Calendar} alt="calendar" style={{ width: '36px', height: '36px' }} />
                                </button>

                                {showCalendar && (
                                    <div
                                        className="position-absolute end-0 mt-2 bg-white rounded shadow p-3"
                                        style={{
                                            width: 'auto',
                                            maxWidth: '300px',
                                            zIndex: 1000,
                                            right: 0
                                        }}
                                    >
                                        <style>
                                            {`
                                            .date-range {
                                                font-size: 12px !important;
                                            }
                                            .date-range .rdrMonth {
                                                width: 240px !important;
                                                padding: 0 !important;
                                            }
                                            .date-range .rdrCalendarWrapper {
                                                font-size: 12px !important;
                                                width: 240px !important;
                                                border-radius: 8px !important;
                                                overflow: hidden !important;
                                                background: white !important;
                                            }
                                            .date-range .rdrDateDisplayWrapper {
                                                display: none !important;
                                            }
                                            .rdrMonthAndYearWrapper {
                                                padding-top: 5px !important;
                                                height: 40px !important;
                                            }
                                            .rdrMonth {
                                                padding: 0 !important;
                                            }
                                        `}
                                        </style>
                                        <DateRange
                                            editableDateInputs={true}
                                            onChange={handleDateRangeChange}
                                            moveRangeOnFirstSelection={false}
                                            ranges={dateRange}
                                            maxDate={new Date()}
                                            months={1}
                                            direction="horizontal"
                                            className="date-range"
                                            staticRanges={[]}
                                            inputRanges={[]}
                                            rangeColors={['#0066ff']}
                                            minDate={new Date("2000-01-01")}
                                            showSelectionPreview={true}
                                            showMonthAndYearPickers={true}
                                            monthDisplayFormat="MMMM yyyy"
                                            dayDisplayFormat="d"
                                            showDateDisplay={false}
                                            monthsShown={1}
                                        />
                                        <div className="d-flex justify-content-end mt-2">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={clearDateFilter}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Filter Component */}
                            <div className="position-relative" ref={dropdownRef}>
                                <button
                                    className="bg-transparent border-0"
                                    onClick={() => setShowDropdown(!showDropdown)}
                                >
                                    <img src={Filter} alt="filter" style={{ width: '36px', height: '36px' }} />
                                </button>

                                {showDropdown && (
                                    <div className="position-absolute end-0 mt-2 bg-white rounded shadow p-2" style={{ width: '200px', zIndex: 1000 }}>
                                        <style>
                                            {`
                                                .form-check-label {
                                                    font-weight: 400 !important;
                                                }
                                                .form-check-input[type=checkbox] {
                                                    border-radius: 0 !important;
                                                }
                                            `}
                                        </style>
                                        <Form>
                                            {filterOptions.map((option) => (
                                                <Form.Check
                                                    key={option.value}
                                                    type="checkbox"
                                                    name="filterOption"
                                                    id={`filter-${option.value}`}
                                                    label={option.label}
                                                    checked={selectedFilter === option.value}
                                                    onChange={() => handleFilterChange(option.value)}
                                                />
                                            ))}
                                        </Form>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody className='pt-0'>
                                {selectedRows.length > 0 && (
                                    <div className={`d-flex align-items-center justify-content-between bg-light-info p-2`}>
                                        <h4 className="text-muted m-0">Delete Selected Experiences?</h4>
                                        <Btn attrBtn={{ color: 'danger', onClick: handleMultiDelete }}>Delete</Btn>
                                    </div>
                                )}
                                <DataTable
                                    className='data-tables theme-scrollbar pt-4'
                                    data={data}
                                    columns={tableColumns.map(col => ({
                                        ...col,
                                        selector: typeof col.selector === 'function' 
                                            ? (row, index) => col.selector(row, index, currentPage, perPage)
                                            : col.selector
                                    }))}
                                    striped={true}
                                    center={true}
                                    pagination
                                    paginationServer={false}
                                    paginationTotalRows={data.length}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    selectableRows
                                    onSelectedRowsChange={handleRowSelected}
                                    clearSelectedRows={toggleDelete}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default ExperienceTable;